import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { BsPlusLg as PlusLg, BsPencil, BsTrash } from 'react-icons/bs';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { ActionStyles } from '@/components/Admin/reusableStyles';
import { TableCSS } from '../styles/MembersStyled';
import { getTeamIdFromURL } from '@/_helpers';
import { connect } from 'react-redux';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import Swal from 'sweetalert2';
import ActiveArchiveToggle from '@/components/Filters/ActiveArchiveToggle';
import { callCenterApi } from '@/_services/api/callCenter';
import { BiArchiveIn, BiArchiveOut } from 'react-icons/bi';

const VerticalStatusBadge = ({ status }) => {
    const map = {
        active: 'success',
        archived: 'danger',
    };
    const className = map[status] || 'warning';

    return <span className={`badge badge-${className}`}>{status}</span>;
};

const ItemRow = ({ sn, item, onEdit, onDelete, isDeleting, deletingId }) => {
    const isItemDeleting = isDeleting && deletingId === item?._id;

    return (
        <tr key={item?._id}>
            <td>{sn}.</td>
            <td>{item?.name}</td>
            <td>{<VerticalStatusBadge status={item?.status} />}</td>
            <td>
                <div className="d-flex gap-2">
                    <ActionStyles>
                        <BootstrapToolTip text="Edit">
                            <BsPencil
                                className={`icon edit ${isItemDeleting ? 'disabled' : ''}`}
                                onClick={() => !isItemDeleting && onEdit(item)}
                            />
                        </BootstrapToolTip>

                        <div>
                            {isItemDeleting ? (
                                <VscLoading className="spin" />
                            ) : (
                                <Fragment>
                                    <BootstrapToolTip text={item?.status === 'archived' ? 'Unarchive' : 'Archive'}>
                                        {item?.status === 'archived' ? (
                                            <BiArchiveOut className="icon archiveOut" onClick={() => onDelete(item)} />
                                        ) : (
                                            <BiArchiveIn className="icon archiveIn" onClick={() => onDelete(item)} />
                                        )}
                                    </BootstrapToolTip>
                                </Fragment>
                            )}
                        </div>
                    </ActionStyles>
                </div>
            </td>
        </tr>
    );
};

const Component = () => {
    const teamId = getTeamIdFromURL();

    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    // Data states
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isArchived, setIsArchived] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
    });
    const [formErrors, setFormErrors] = useState({});

    const filterItems = useCallback((itemsList, searchValue) => {
        if (!itemsList || itemsList.length === 0) return [];

        return itemsList.filter((item) => {
            const searchMatch =
                searchValue.trim() === '' || item?.name?.toLowerCase().includes(searchValue.toLowerCase());

            return searchMatch;
        });
    }, []);

    const fetchData = useCallback(async () => {
        if (!teamId) return;

        setIsLoading(true);
        try {
            const response =
                (await callCenterApi.getVerticals({ team: teamId, status: isArchived ? 'archived' : 'active' })) || {};
            const fetchedItems = response?.results || [];
            setItems(fetchedItems);
            setFilteredItems(filterItems(fetchedItems, searchText));
        } catch (error) {
            if (error?.name === 'AbortError') return;
            toast.error(error?.message || 'Failed to fetch verticals');
        } finally {
            setIsLoading(false);
        }
    }, [teamId, filterItems, isArchived]);

    const createData = async (payload) => {
        setIsSaving(true);
        try {
            const newItem = await callCenterApi.createVertical(payload);

            setItems((prev) => {
                const updatedItems = [newItem, ...prev];
                setFilteredItems(filterItems(updatedItems, searchText));
                return updatedItems;
            });

            toast.success(`Vertical "${newItem.name}" created successfully!`);
            return newItem;
        } catch (err) {
            toast.error(err?.message || `Failed to create vertical`);
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const updateData = async (verticalId, payload) => {
        setIsSaving(true);
        try {
            const updatedItem = await callCenterApi.updateVertical(verticalId, payload);

            setItems((prev) => {
                const updatedItems = prev.map((item) => (item._id === verticalId ? { ...item, ...updatedItem } : item));
                setFilteredItems(filterItems(updatedItems, searchText, isArchived));
                return updatedItems;
            });

            toast.success(`Vertical "${updatedItem.name}" updated successfully!`);
            return updatedItem;
        } catch (err) {
            toast.error(err?.message || `Failed to update vertical`);
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const updateDataStatus = async (item) => {
        setDeletingId(item._id);
        setIsDeleting(true);

        try {
            const status = item?.status === 'archived' ? 'active' : 'archived';
            const result = await Swal.fire({
                title: `Are you sure you want to ${status} "${item.name}"?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: `Yes, ${status} it!`,
                cancelButtonText: 'Cancel',
            });

            if (result.isConfirmed) {
                const payload = { status };
                await callCenterApi.updateVerticalStatus(item._id, payload);
                await fetchData();
                toast.success(`Vertical "${item.name}" ${status}d successfully!`);
            }
        } catch (err) {
            toast.error(err?.message || `Failed to update status`);
        } finally {
            setIsDeleting(false);
            setDeletingId(null);
        }
    };

    useEffect(() => {
        fetchData();
    }, [teamId, fetchData]);

    useEffect(() => {
        if (items.length > 0) {
            setFilteredItems(filterItems(items, searchText, isArchived));
        }
    }, [searchText, isArchived, items, filterItems]);

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const openCreateModal = () => {
        setEditingItem(null);
        setFormData({ name: '' });
        setFormErrors({});
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({ name: item.name || '' });
        setFormErrors({});
        setShowModal(true);
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            const payload = { name: formData.name.trim() };

            if (editingItem) {
                await updateData(editingItem._id, payload);
            } else {
                payload.team = teamId;
                await createData(payload);
            }

            setShowModal(false);
        } catch (err) {
            // Error is already handled in the updateData/createData functions
            console.error('Save error:', err);
        }
    };

    const handleModalClose = () => {
        if (!isSaving) {
            setShowModal(false);
        }
    };

    const isBusy = isLoading || isSaving || isDeleting;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <input
                    className="form-control"
                    onChange={handleSearchChange}
                    value={searchText}
                    placeholder="Search verticals..."
                    disabled={isLoading}
                    style={{
                        width: '250px',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        padding: '6px 10px',
                    }}
                />
                <div className="d-flex align-items-center gap-2">
                    <ActiveArchiveToggle
                        isActive={!isArchived}
                        setIsActive={(val) => !isBusy && setIsArchived(!val)}
                        disabled={isLoading}
                    />
                    <Button variant="primary" onClick={openCreateModal} disabled={isBusy} className="ml-2">
                        {isSaving && !showModal ? <VscLoading className="spin mr-2" /> : <PlusLg className="mr-2" />}
                        New Vertical
                    </Button>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="text-center py-5">
                    <VscLoading className="spin" fontSize="42" />
                    <p className="text-muted mt-2">Loading verticals...</p>
                </div>
            ) : (
                <div className="custom-card mt-3">
                    <TableCSS>
                        <thead>
                            <tr>
                                <th style={{ width: 10 }}>S.N.</th>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.length > 0 ? (
                                filteredItems.map((item, index) => (
                                    <ItemRow
                                        sn={index + 1}
                                        key={item._id}
                                        item={item}
                                        onEdit={openEditModal}
                                        onDelete={updateDataStatus}
                                        isDeleting={isDeleting}
                                        deletingId={deletingId}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">
                                        <div className="text-center py-4">
                                            <h3 className="text-muted mb-0">
                                                No {isArchived ? 'Archived ' : 'Active '}Verticals Found
                                            </h3>
                                            {searchText && <p className="text-muted mt-2">Try adjusting your search</p>}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </TableCSS>
                </div>
            )}

            {/* Modal */}
            <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingItem ? 'Edit Vertical' : 'New Vertical'}
                        {isSaving && <VscLoading className="spin ml-2" />}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            value={formData.name}
                            onChange={(e) => {
                                setFormData((prev) => ({ ...prev, name: e.target.value }));
                                if (formErrors.name) {
                                    setFormErrors((prev) => ({ ...prev, name: '' }));
                                }
                            }}
                            placeholder="Enter vertical name"
                            isInvalid={!!formErrors.name}
                            disabled={isSaving}
                            required
                        />
                        <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={isSaving || !formData.name.trim()}>
                        {isSaving && <VscLoading className="spin mr-2" />}
                        {editingItem ? 'Update' : 'Create'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state) => ({});

const CampaignsTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, {})(CampaignsTab);
